<template>
  <ExpenseReceiptsPageShell
    :title="text.page.title"
    :subtitle="text.page.subtitle"
    :actions="headerActions"
    @action="onHeaderAction"
  >
    <template #metrics>
      <section class="metrics-grid payment-receipts-kpis-grid" :style="kpiGridStyle">
        <ExpenseReceiptsKpiCard
          v-for="metric in kpiCards"
          :key="metric.title"
          :title="metric.title"
          :value="metric.value"
          :value-meta-items="metric.valueMetaItems"
          :value-meta-lines="metric.valueMetaLines"
          :value-segments="metric.valueSegments"
          :value-scale="metric.valueScale"
          :icon="metric.icon"
          :icon-bg="metric.iconBg"
          :icon-color="metric.iconColor"
        />
      </section>
    </template>

    <template #toolbar>
      <ExpenseReceiptsToolbar
        :filter-options="filterOptions"
        :labels="text.toolbar"
        @search-change="handleToolbarSearch"
        @filters-change="handleToolbarFilters"
        @apply="applyToolbarFilters"
      />
    </template>

    <template #tabs>
      <StatusTabs
        :tabs="statusTabs"
        :active-tab="activeStatusTab"
        @select="selectStatusTab"
      />
    </template>

    <template #table>
      <ExpenseReceiptsTable
        :rows="paginatedRows"
        :loading="tableLoading"
        :pagination="pagination"
        :active-status-tab="activeStatusTab"
        :labels="text.table"
        :loading-text="text.table.loading"
        :empty-text="text.table.empty"
        :sort-field="sortField"
        :sort-direction="sortDirection"
        @view-expense-receipt="viewExpenseReceipt"
        @download-files="downloadFiles"
        @download-xml="downloadXml"
        @download-pdf="downloadPdf"
        @page-change="handlePageChange"
        @sort-change="handleSortChange"
      />
    </template>

    <template #modal>
      <ExportFormatModal
        :show="isExpenseReceiptExportModalOpen"
        :filters="exportFilters"
        :search-term="appliedToolbarSearch"
        :page-customer-ids="currentPageExpenseReceiptIds"
        :text-catalog="text"
        :export-service="exportService"
        file-name-prefix="pagos_egresos"
        @close="closeExpenseReceiptExportModal"
      />

      <DocumentsDownloadModal
        :show="showDownloadModal"
        :labels="text.downloadModal"
        :context="downloadScopeContext"
        :latest-request="latestDownloadRequest"
        :package-limit="downloadPackageLimit"
        :expires-hours="downloadExpiresHours"
        :submitting="downloadRequestSubmitting"
        :document-type-options="['pdf']"
        :allow-group-by="true"
        @close="closeDownloadModal"
        @refresh="refreshLatestDownloadRequest"
        @create="createDownloadRequest"
        @download-package="downloadRequestPackage"
        @retry-package="retryDownloadRequestPackage"
        @regenerate="regenerateDownloadRequest"
      />
    </template>
  </ExpenseReceiptsPageShell>
</template>

<script>
import ExpenseReceiptsPageShell from '@/components/expenseReceipts/ExpenseReceiptsPageShell.vue';
import ExpenseReceiptsToolbar from '@/components/expenseReceipts/ExpenseReceiptsToolbar.vue';
import ExpenseReceiptsTable from '@/components/expenseReceipts/ExpenseReceiptsTable.vue';
import ExpenseReceiptsKpiCard from '@/components/expenseReceipts/ExpenseReceiptsKpiCard.vue';
import DocumentsDownloadModal from '@/components/common/downloads/DocumentsDownloadModal.vue';
import StatusTabs from '@/components/common/StatusTabs.vue';
import ExportFormatModal from '@/components/common/exports/ExportFormatModal.vue';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  createDefaultExpenseReceiptsToolbarFilters,
  normalizeExpenseReceiptsToolbarFilters,
  filterExpenseReceiptsRowsByToolbar,
} from '@/composables/expenseReceipts/useExpenseReceiptsFilters';
import {
  buildExpenseReceiptStatusTabs,
  filterExpenseReceiptsByStatus,
} from '@/composables/expenseReceipts/useExpenseReceiptsStatusTabs';
import {
  buildExpenseReceiptsKpiCards,
  createInitialExpenseReceiptsMetricsValues,
  mapExpenseReceiptsMetricsPayload,
} from '@/composables/expenseReceipts/useExpenseReceiptsMetrics';
import {
  createDefaultExpenseReceiptsFilterOptions,
  buildExpenseReceiptsListParams,
  fetchExpenseReceiptsFilterOptions,
  fetchExpenseReceiptsSnapshot,
} from '@/composables/expenseReceipts/useExpenseReceiptsList';
import expenseReceiptsService from '@/services/expenseReceipts';
import { extractApiErrorMessage, extractPayload } from '@/utils/dataNormalization';
import { downloadBlobFile } from '@/utils/fileDownload';
import {
  resolveTotalPages,
  ensureValidPage,
  sliceRowsByPage,
  buildPaginationPayload,
} from '@/composables/shared/usePagination';
import { resolveSortChange, sortRows } from '@/composables/shared/useSorting';
import { formatDateShort } from '@/utils/datePresentation';

const { catalog: expenseReceiptsText } = useDomainText('expenseReceipts');
const DOWNLOAD_REQUEST_POLLING_MS = 15000;

export default {
  name: 'ExpenseReceiptsPage',
  components: {
    ExpenseReceiptsPageShell,
    ExpenseReceiptsToolbar,
    ExpenseReceiptsTable,
    ExpenseReceiptsKpiCard,
    DocumentsDownloadModal,
    StatusTabs,
    ExportFormatModal,
  },
  data() {
    return {
      text: expenseReceiptsText,
      isExpenseReceiptExportModalOpen: false,
      tableLoading: false,
      rows: [],
      filterOptions: createDefaultExpenseReceiptsFilterOptions(),
      toolbarSearch: '',
      toolbarFilters: createDefaultExpenseReceiptsToolbarFilters(),
      appliedToolbarSearch: '',
      appliedToolbarFilters: createDefaultExpenseReceiptsToolbarFilters(),
      activeStatusTab: 'all',
      metricsValues: createInitialExpenseReceiptsMetricsValues(),
      currentPage: 1,
      pageSize: 20,
      sortField: 'date',
      sortDirection: 'desc',
      listRequestToken: 0,
      showDownloadModal: false,
      downloadRequestSubmitting: false,
      latestDownloadRequest: null,
      downloadPackageLimit: 200,
      downloadExpiresHours: 24,
      downloadRequestPollingId: null,
    };
  },
  computed: {
    exportService() {
      return expenseReceiptsService;
    },
    headerActions() {
      return [
        {
          key: 'payment',
          label: this.text.headerActions.payment,
          icon: 'fa-credit-card',
          variant: 'success',
        },
        {
          key: 'export',
          label: this.text.headerActions.export,
          icon: 'fa-download',
          variant: 'secondary',
          className: 'app-header-btn--export',
        },
        {
          key: 'download',
          label: this.text.headerActions.exportDownload,
          icon: 'fa-download',
          variant: 'secondary',
          className: 'app-header-btn--download-list',
        },
      ];
    },
    toolbarFilteredRows() {
      return filterExpenseReceiptsRowsByToolbar(this.rows, {
        search: this.appliedToolbarSearch,
        filters: this.appliedToolbarFilters,
      });
    },
    statusTabs() {
      return buildExpenseReceiptStatusTabs(this.toolbarFilteredRows, this.text.tabs || {});
    },
    statusFilteredRows() {
      return filterExpenseReceiptsByStatus(this.toolbarFilteredRows, this.activeStatusTab);
    },
    sortedRows() {
      return sortRows(this.statusFilteredRows, {
        field: this.sortField,
        direction: this.sortDirection,
      }, {
        date: (row) => new Date(row?.date || 0).getTime(),
        supplier: (row) => String(
          row?.supplier_name
          || row?.collaborator_name
          || row?.collaborator_full_name
          || ''
        ).trim().toLowerCase(),
        total: (row) => Number(row?.total || 0),
      });
    },
    totalPages() {
      return resolveTotalPages(this.sortedRows.length, this.pageSize);
    },
    paginatedRows() {
      return sliceRowsByPage(this.sortedRows, this.currentPage, this.pageSize);
    },
    pagination() {
      return buildPaginationPayload({
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        totalItems: this.sortedRows.length,
      });
    },
    currentPageExpenseReceiptIds() {
      return (Array.isArray(this.paginatedRows) ? this.paginatedRows : [])
        .map((row) => Number(row?.id || 0))
        .filter((id) => id > 0);
    },
    exportFilters() {
      return {
        ...(this.appliedToolbarFilters || {}),
        status_tab: String(this.activeStatusTab || 'all').trim() || 'all',
      };
    },
    kpiCards() {
      return buildExpenseReceiptsKpiCards(this.metricsValues, this.text.metrics || {});
    },
    kpiGridStyle() {
      const totalCards = Array.isArray(this.kpiCards) ? this.kpiCards.length : 0;
      const columns = Math.min(Math.max(totalCards, 1), 4);

      return {
        '--payment-receipts-kpi-columns': String(columns),
      };
    },
    activeStatusTabLabel() {
      const activeTab = (Array.isArray(this.statusTabs) ? this.statusTabs : [])
        .find((tab) => String(tab?.key || '') === String(this.activeStatusTab || 'all'));

      return String(activeTab?.label || this.text?.tabs?.all || '').trim();
    },
    downloadScopeLabel() {
      const parts = ['Pagos de egresos'];
      if (this.activeStatusTabLabel !== '') {
        parts.push(this.activeStatusTabLabel);
      }

      const dateFrom = String(this.appliedToolbarFilters?.date_from || '').trim();
      const dateTo = String(this.appliedToolbarFilters?.date_to || '').trim();
      if (dateFrom !== '' || dateTo !== '') {
        const dateFromLabel = dateFrom !== '' ? formatDateShort(dateFrom, { fallback: dateFrom }) : '...';
        const dateToLabel = dateTo !== '' ? formatDateShort(dateTo, { fallback: dateTo }) : '...';
        parts.push(`${dateFromLabel} - ${dateToLabel}`);
      }

      return parts.join(' · ');
    },
    downloadScopeContext() {
      return {
        totalInvoices: this.statusFilteredRows.length,
        scopeDescription: String(this.text?.downloadModal?.contextMessage || '').trim(),
        scopeLabel: this.downloadScopeLabel,
      };
    },
  },
  mounted() {
    this.loadRows();
    this.loadFilterOptions();
  },
  beforeUnmount() {
    this.stopDownloadRequestPolling();
  },
  methods: {
    buildListParams() {
      return buildExpenseReceiptsListParams({
        toolbarSearch: this.appliedToolbarSearch,
        toolbarFilters: this.appliedToolbarFilters,
      });
    },
    async loadRows() {
      const requestToken = ++this.listRequestToken;
      this.tableLoading = true;

      try {
        const snapshot = await fetchExpenseReceiptsSnapshot(this.buildListParams());

        if (requestToken === this.listRequestToken) {
          const rows = Array.isArray(snapshot?.rows) ? snapshot.rows : [];
          this.rows = rows;
          this.metricsValues = mapExpenseReceiptsMetricsPayload(snapshot?.metrics || {});
          this.currentPage = 1;
        }
      } catch (error) {
        if (requestToken === this.listRequestToken) {
          this.rows = [];
          this.metricsValues = createInitialExpenseReceiptsMetricsValues();
        }
      } finally {
        if (requestToken === this.listRequestToken) {
          this.tableLoading = false;
        }
      }
    },
    async loadFilterOptions() {
      try {
        const nextOptions = await fetchExpenseReceiptsFilterOptions();
        this.filterOptions = {
          ...createDefaultExpenseReceiptsFilterOptions(),
          ...(nextOptions || {}),
        };
      } catch (error) {
        this.filterOptions = createDefaultExpenseReceiptsFilterOptions();
      }
    },
    onHeaderAction(action) {
      const actionKey = String(action?.key || '').trim();

      if (actionKey === 'payment') {
        this.$router.push({ name: 'expenseReceiptCreate' });
        return;
      }

      if (actionKey === 'export') {
        this.isExpenseReceiptExportModalOpen = true;
        return;
      }

      if (actionKey === 'download') {
        this.openDownloadModal();
      }
    },
    normalizeLatestDownloadRequest(payload) {
      const safePayload = payload && typeof payload === 'object' ? payload : {};
      const request = safePayload?.request && typeof safePayload.request === 'object'
        ? safePayload.request
        : null;

      this.downloadPackageLimit = Number(safePayload?.package_limit || this.downloadPackageLimit || 200) || 200;
      this.downloadExpiresHours = Number(safePayload?.expires_hours || this.downloadExpiresHours || 24) || 24;
      this.latestDownloadRequest = request;
    },
    async refreshLatestDownloadRequest() {
      try {
        const response = await expenseReceiptsService.downloadRequestsLatest({}, { skipLoading: true });
        const payload = extractPayload(response);
        this.normalizeLatestDownloadRequest(payload);
      } catch (error) {
        // Keep modal usable even when latest request cannot be loaded.
      }

      this.syncDownloadRequestPolling();
    },
    openDownloadModal() {
      this.showDownloadModal = true;
      this.refreshLatestDownloadRequest();
    },
    closeDownloadModal() {
      this.showDownloadModal = false;
      this.stopDownloadRequestPolling();
    },
    hasActiveDownloadProcessing() {
      const status = String(this.latestDownloadRequest?.status || '').trim().toLowerCase();
      return status === 'pending' || status === 'processing';
    },
    stopDownloadRequestPolling() {
      if (this.downloadRequestPollingId) {
        window.clearInterval(this.downloadRequestPollingId);
        this.downloadRequestPollingId = null;
      }
    },
    syncDownloadRequestPolling() {
      const shouldPoll = this.showDownloadModal && this.hasActiveDownloadProcessing();
      if (!shouldPoll) {
        this.stopDownloadRequestPolling();
        return;
      }

      if (this.downloadRequestPollingId) {
        return;
      }

      this.downloadRequestPollingId = window.setInterval(() => {
        if (!this.showDownloadModal) {
          this.stopDownloadRequestPolling();
          return;
        }

        this.refreshLatestDownloadRequest();
      }, DOWNLOAD_REQUEST_POLLING_MS);
    },
    buildDownloadRequestPayload(groupBy = 'none') {
      return {
        document_type: 'pdf',
        group_by: String(groupBy || 'none').trim().toLowerCase() || 'none',
        tab_key: String(this.activeStatusTab || 'all').trim().toLowerCase() || 'all',
        tab_label: this.activeStatusTabLabel,
        scope_label: this.downloadScopeLabel,
        search: String(this.appliedToolbarSearch || '').trim(),
        list_params: this.buildListParams(),
      };
    },
    resolveDownloadFileNameFromHeaders(headers = {}, fallback = 'pagos_egresos_paquete_1_de_1.zip') {
      const contentDisposition = String(headers?.['content-disposition'] || headers?.['Content-Disposition'] || '').trim();
      if (contentDisposition === '') {
        return fallback;
      }

      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      if (utf8Match && utf8Match[1]) {
        try {
          return decodeURIComponent(String(utf8Match[1]).trim());
        } catch (error) {
          return String(utf8Match[1]).trim() || fallback;
        }
      }

      const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      return String(basicMatch?.[1] || '').trim() || fallback;
    },
    async createDownloadRequest(payload = {}) {
      if (this.downloadRequestSubmitting) {
        return;
      }

      this.downloadRequestSubmitting = true;
      const requestPayload = this.buildDownloadRequestPayload(payload?.groupBy || 'none');
      const totalRows = Number(this.statusFilteredRows.length || 0) || 0;
      const isSinglePackageScope = totalRows > 0 && totalRows <= Number(this.downloadPackageLimit || 200);

      try {
        if (isSinglePackageScope) {
          const response = await expenseReceiptsService.downloadRequestsDirectDownload(requestPayload);
          const fileName = this.resolveDownloadFileNameFromHeaders(response?.headers || {}, 'pagos_egresos_paquete_1_de_1.zip');
          downloadBlobFile(response?.data ?? response, fileName, 'application/zip');
          await this.refreshLatestDownloadRequest();
          this.closeDownloadModal();
          return;
        }

        const response = await expenseReceiptsService.downloadRequestsStore(requestPayload);
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.normalizeLatestDownloadRequest(extractPayload(response));
        this.$toast?.success(String(this.text?.toasts?.downloadRequestCreated || ''));
      } catch (error) {
        const fallback = String(this.text?.toasts?.downloadRequestError || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      } finally {
        this.downloadRequestSubmitting = false;
        this.syncDownloadRequestPolling();
      }
    },
    async regenerateDownloadRequest(payload = {}) {
      const requestId = Number(this.latestDownloadRequest?.id || 0) || 0;
      if (requestId <= 0 || this.downloadRequestSubmitting) {
        return;
      }

      this.downloadRequestSubmitting = true;

      try {
        const response = await expenseReceiptsService.downloadRequestsRegenerate(requestId, {
          document_type: 'pdf',
          group_by: String(payload?.groupBy || 'none').trim().toLowerCase() || 'none',
        });
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.normalizeLatestDownloadRequest(extractPayload(response));
        this.$toast?.success(String(this.text?.toasts?.regenerateSuccess || ''));
      } catch (error) {
        const fallback = String(this.text?.toasts?.regenerateError || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      } finally {
        this.downloadRequestSubmitting = false;
        this.syncDownloadRequestPolling();
      }
    },
    resolveDownloadPackageFileName(packageRow = {}) {
      const rawName = String(packageRow?.file_name || '').trim();
      if (rawName !== '') {
        return rawName;
      }

      const index = Number(packageRow?.package_index || 1) || 1;
      const total = Number(this.latestDownloadRequest?.total_packages || 1) || 1;
      return `pagos_egresos_paquete_${index}_de_${total}.zip`;
    },
    async downloadRequestPackage(packageRow = {}) {
      const requestId = Number(this.latestDownloadRequest?.id || 0) || 0;
      const packageId = Number(packageRow?.id || 0) || 0;
      if (requestId <= 0 || packageId <= 0 || this.downloadRequestSubmitting) {
        return;
      }

      this.downloadRequestSubmitting = true;

      try {
        const response = await expenseReceiptsService.downloadRequestsDownloadPackage(requestId, packageId);
        const fileName = this.resolveDownloadPackageFileName(packageRow);
        downloadBlobFile(response?.data ?? response, fileName, 'application/zip');
        await this.refreshLatestDownloadRequest();
      } catch (error) {
        const fallback = String(this.text?.toasts?.downloadPackageError || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
        await this.refreshLatestDownloadRequest();
      } finally {
        this.downloadRequestSubmitting = false;
      }
    },
    async retryDownloadRequestPackage(packageRow = {}) {
      const requestId = Number(this.latestDownloadRequest?.id || 0) || 0;
      const packageId = Number(packageRow?.id || 0) || 0;
      if (requestId <= 0 || packageId <= 0 || this.downloadRequestSubmitting) {
        return;
      }

      this.downloadRequestSubmitting = true;

      try {
        const response = await expenseReceiptsService.downloadRequestsRetryPackage(requestId, packageId);
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.normalizeLatestDownloadRequest(extractPayload(response));
        this.$toast?.success(String(this.text?.toasts?.retryPackageSuccess || ''));
      } catch (error) {
        const fallback = String(this.text?.toasts?.retryPackageError || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      } finally {
        this.downloadRequestSubmitting = false;
        this.syncDownloadRequestPolling();
      }
    },
    closeExpenseReceiptExportModal() {
      this.isExpenseReceiptExportModalOpen = false;
    },
    handleToolbarSearch(searchValue) {
      const nextSearch = String(searchValue || '');
      this.toolbarSearch = nextSearch;
      this.appliedToolbarSearch = nextSearch;
      this.currentPage = 1;
    },
    handleToolbarFilters(filters) {
      const previousDateStart = String(this.toolbarFilters?.date_from || '').trim();
      const previousDateEnd = String(this.toolbarFilters?.date_to || '').trim();
      const normalizedFilters = normalizeExpenseReceiptsToolbarFilters(filters);

      this.toolbarFilters = normalizedFilters;

      const nextDateStart = String(normalizedFilters?.date_from || '').trim();
      const nextDateEnd = String(normalizedFilters?.date_to || '').trim();
      const dateChanged = nextDateStart !== previousDateStart || nextDateEnd !== previousDateEnd;

      if (dateChanged) {
        this.appliedToolbarSearch = String(this.toolbarSearch || '').trim();
        this.appliedToolbarFilters = normalizedFilters;
        this.loadRows();
      }
    },
    applyToolbarFilters(payload) {
      const nextSearch = String(payload?.search ?? this.toolbarSearch ?? '').trim();
      const nextFilters = normalizeExpenseReceiptsToolbarFilters(payload?.filters ?? this.toolbarFilters);
      const previousFiltersSerialized = JSON.stringify(this.appliedToolbarFilters || {});
      const nextFiltersSerialized = JSON.stringify(nextFilters || {});
      const filtersChanged = previousFiltersSerialized !== nextFiltersSerialized;

      this.appliedToolbarSearch = nextSearch;
      this.appliedToolbarFilters = nextFilters;

      if (filtersChanged) {
        this.loadRows();
      }
    },
    selectStatusTab(tabKey) {
      this.activeStatusTab = String(tabKey || 'all').trim() || 'all';
      this.currentPage = 1;
    },
    handlePageChange(nextPage) {
      this.currentPage = ensureValidPage(nextPage, this.totalPages);
    },
    handleSortChange(payload) {
      const sort = resolveSortChange({
        currentField: this.sortField,
        currentDirection: this.sortDirection,
        requestedField: payload?.field,
        requestedDirection: payload?.direction,
      });

      this.sortField = sort.field;
      this.sortDirection = sort.direction;
      this.currentPage = 1;
    },
    async viewExpenseReceipt(row) {
      const expenseReceiptId = Number(row?.id || 0) || 0;
      if (expenseReceiptId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      try {
        await expenseReceiptsService.show(expenseReceiptId, {}, { skipLoading: true });
        this.$router.push({
          name: 'expenseReceiptDetail',
          params: {
            expenseReceiptId: String(expenseReceiptId),
          },
        });
      } catch (error) {
        const fallback = String(this.text?.toasts?.openDetailError || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      }
    },
    downloadFiles(row) {
      return this.downloadExpenseReceiptZip(row);
    },
    async downloadXml(row) {
      const expenseReceiptId = Number(row?.id || 0) || 0;
      if (expenseReceiptId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      if (!this.isStampedExpenseReceiptRow(row)) {
        return;
      }

      try {
        const response = await expenseReceiptsService.downloadXml(expenseReceiptId);
        const fileBaseName = this.resolveExpenseReceiptDocumentBaseName(row || {});
        downloadBlobFile(response?.data ?? response, `${fileBaseName}.xml`, 'application/xml');
      } catch (error) {
        const fallback = String(this.text?.toasts?.downloadXml(this.resolveExpenseReceiptReference(row)) || 'No fue posible descargar el XML del pago.');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      }
    },
    downloadPdf(row) {
      return this.downloadExpenseReceiptPdf(row);
    },
    resolveExpenseReceiptDocumentBaseName(row = {}) {
      const folio = String(row?.folio || '').trim();
      const id = Number(row?.id || 0) || 0;
      const fallback = id > 0 ? `pago_${id}` : 'pago';

      return (folio || fallback)
        .replace(/\s+/g, '_')
        .replace(/[^A-Za-z0-9_\-]/g, '') || fallback;
    },
    async downloadExpenseReceiptPdf(row) {
      const expenseReceiptId = Number(row?.id || 0) || 0;
      if (expenseReceiptId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      try {
        const response = await expenseReceiptsService.downloadPdf(expenseReceiptId);
        this.openPdfBlob(response?.data ?? response, row);
      } catch (error) {
        const fallback = String(this.text?.toasts?.downloadPdf(this.resolveExpenseReceiptReference(row)) || '');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      }
    },
    async downloadExpenseReceiptZip(row) {
      const expenseReceiptId = Number(row?.id || 0) || 0;
      if (expenseReceiptId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      if (!this.isStampedExpenseReceiptRow(row)) {
        return;
      }

      try {
        const response = await expenseReceiptsService.downloadZip(expenseReceiptId);
        const fileBaseName = this.resolveExpenseReceiptDocumentBaseName(row || {});
        downloadBlobFile(response?.data ?? response, `${fileBaseName}_cfdi.zip`, 'application/zip');
      } catch (error) {
        const fallback = String(this.text?.toasts?.downloadFiles(this.resolveExpenseReceiptReference(row)) || 'No fue posible descargar el ZIP del pago.');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      }
    },
    isStampedExpenseReceiptRow(row = {}) {
      const stampedFlag = Number(row?.is_stamped || 0) === 1;
      const cfdiUuid = String(row?.payment_cfdi_uuid || '').trim();
      return stampedFlag || cfdiUuid !== '';
    },
    openPdfBlob(payload, row = null) {
      const blob = payload instanceof Blob
        ? payload
        : new Blob([payload], { type: 'application/pdf' });

      const objectUrl = window.URL.createObjectURL(blob);
      const opened = window.open(objectUrl, '_blank', 'noopener');

      if (!opened) {
        const fileBaseName = this.resolveExpenseReceiptDocumentBaseName(row || {});
        downloadBlobFile(blob, `${fileBaseName}.pdf`, 'application/pdf');
      }

      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 60 * 1000);
    },
    resolveExpenseReceiptReference(row) {
      const fallback = String(this.text?.toasts?.paymentReferenceFallback || '').trim();
      const folio = String(row?.folio || '').trim();
      return folio || fallback;
    },
  },
};
</script>

<style scoped>
.payment-receipts-kpis-grid {
  grid-template-columns: repeat(var(--payment-receipts-kpi-columns, 4), minmax(0, 1fr));
}

@media (max-width: 1200px) {
  .payment-receipts-kpis-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .payment-receipts-kpis-grid {
    grid-template-columns: 1fr;
  }
}
</style>
